import React, { useMemo, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { mortgageApi, reApi } from '../workspacesApi';
import { errorMessage } from '../errors';
import { MORTGAGE_FIELDS, reFields, buildForm, diffPayload, OppFields } from './oppFields';

// "Details" section of the card drawer, shared by both boards (ticket 187): the read-only view
// (`children`) with an Edit button that swaps in the form. Saves only what changed through the
// card's PATCH route and hands the saved card to onSaved(updated).
//
// `locked` = field keys the server owns on a mirrored (GHL / Arive) card: shown read-only, each with
// `lockNote` ({ text, href, link }: "Managed in GoHighLevel" + the link out) right beside it, never
// sent. A 409 from the server locks the fields it names for the rest of the drawer's life, with the
// note taken from the 409 when the card did not already carry one.
export default function OpportunityEditSection({ kind, opp, lenders = [], locked = [], lockNote = null, onSaved, children }) {
  const api = kind === 'mortgage' ? mortgageApi : reApi;
  const fields = useMemo(() => (kind === 'mortgage' ? MORTGAGE_FIELDS : reFields(opp.side || 'buyer')), [kind, opp.side]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState([]);
  const [refusedNote, setRefusedNote] = useState(null);
  const lockedSet = useMemo(() => new Set([...locked, ...refused]), [locked, refused]);
  const note = lockNote || refusedNote;

  const start = () => {
    const built = buildForm(fields, opp);
    setInitial(built);
    setForm(built);
    setEditing(true);
  };
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = diffPayload(fields.filter(f => !lockedSet.has(f.key)), form, initial, opp);
    if (Object.keys(payload).length === 0) { setEditing(false); return; }
    setSaving(true);
    try {
      const updated = await api.updateOpp(opp.opportunity_id, payload);
      onSaved?.(updated);
      toast({ title: 'Saved' });
      setEditing(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === 'object' && Array.isArray(d.fields)) {
        // The server owns these on this card: put the old values back and lock them.
        setRefused(prev => [...new Set([...prev, ...d.fields])]);
        setRefusedNote({ text: d.message || 'Managed in GoHighLevel', href: d.edit_url, link: d.code === 'arive_managed' ? 'Open in Arive' : 'Edit in GHL' });
        setForm(f => ({ ...f, ...Object.fromEntries(d.fields.filter(k => k in initial).map(k => [k, initial[k]])) }));
      }
      toast({ title: 'Save failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="drawer-details">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Details</div>
        {!editing && (
          <button type="button" onClick={start} data-testid="drawer-edit-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#070B5F' }}>
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>
      {!editing ? children : (
        <div className="space-y-3" data-testid="drawer-edit-form">
          <OppFields fields={fields} form={form} setField={setField} locked={lockedSet} lockNote={note} lenders={lenders} testPrefix="edit-opp" />
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-outline" onClick={() => setEditing(false)} data-testid="edit-opp-cancel">Cancel</button>
            <button type="button" className="btn-gold" disabled={saving} onClick={save} data-testid="edit-opp-save">
              {saving && <Loader2 className="animate-spin" size={14} />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
